import { modifyStringReplaceAll, removeSpecialCharacters } from '@libs/core';

import type {
    ProductCategories,
    ProductCategory,
    ProductHit,
    ProductHitPrice,
    ProductPromo,
    ProductSize,
} from '../types';
import { decodeEntityString } from './decodeEntityString';

export const transformPromos = (variations: string[] | undefined): ProductPromo[] | undefined =>
    variations?.reduce((prev: ProductPromo[], promo: string) => [...prev, { description: promo }], []);

export const formatPrice = (price: string) => price.replace(/\$/g, '').trim();

export const isPriceRange = (price?: ProductHitPrice): boolean => {
    if (!price || !price.formatted || !price.formatted.primary) {
        return false;
    }
    const isMarkedAsRange = price.displayType === 'range';
    const primaryHasHyphen = price.formatted.primary.includes('-');
    const strikethroughHasHyphen = price?.formatted?.strikethrough?.includes('-') ?? true;
    return isMarkedAsRange && primaryHasHyphen && strikethroughHasHyphen;
};

export const getPriceVariant = (prices?: ProductHitPrice): 'primary' | 'disabled' | 'sale' => {
    if (prices && prices.formatted && prices.formatted.strikethrough) {
        return 'disabled';
    }

    return 'primary';
};

export const getRangePrices = (price: ProductHitPrice) => {
    if (price.formatted.primary && !price.formatted.strikethrough) {
        const firstPrice = price.formatted.primary.split('-');

        return [formatPrice(firstPrice[0]), formatPrice(firstPrice[1])];
    }

    if (price.formatted.primary && price.formatted.strikethrough) {
        const firstPrice = price.formatted.primary.split('-');
        const secondPrice = price.formatted.strikethrough.split('-');

        return [
            formatPrice(firstPrice[0]),
            formatPrice(firstPrice[1]),
            formatPrice(secondPrice[0]),
            formatPrice(secondPrice[1]),
        ];
    }

    return [0];
};

export const getNonRangePrices = (price: ProductHitPrice) => {
    if (price.formatted.primary && !price.formatted.strikethrough) {
        return [formatPrice(price.formatted.primary)];
    }

    if (price.formatted.primary && price.formatted.strikethrough) {
        return [formatPrice(price.formatted.primary), formatPrice(price.formatted.strikethrough)];
    }

    return [0];
};

export const getPrices = (price: ProductHitPrice | undefined, isRange: boolean): Array<string | number> => {
    if (price) {
        if (isRange) {
            return getRangePrices(price);
        }

        return getNonRangePrices(price);
    }

    return [0];
};

export const getVariants = (variations?: string[]): string => {
    if (!variations) {
        return ' ';
    }

    return variations.join(' ').toLowerCase();
};

export const getReviewCount = (reviewCount?: number) => {
    if (reviewCount) {
        return `(${reviewCount})`;
    }

    return '(0)';
};

export const getPrimaryCategoryList = (
    primaryCategoryId?: string,
    categories?: ProductCategories
): ProductCategory[] => {
    if (!primaryCategoryId || !categories) {
        return [];
    }

    let categoriesToUse: ProductCategory[] = [];

    // find which list of categories is the primary one
    categories.forEach((subCategories: ProductCategory[]) => {
        subCategories.forEach((category: ProductCategory) => {
            if (category.id === primaryCategoryId) {
                categoriesToUse = subCategories;
            }
        });
    });

    return categoriesToUse;
};

export const getItemSize = (size: ProductSize) => {
    let itemSize = '';
    for (const key in size) {
        itemSize += size[key as keyof ProductSize];
    }
    return itemSize;
};

export const toUrlString = (url: string) => {
    const slug = modifyStringReplaceAll(removeSpecialCharacters(url, ['&', ' ', ' - ']), {
        '&': 'and',
        ' - ': '---', // we need to keep --- in url to match legacy URLs
        '@': '',
        ' ': '-',
        ',': '',
        ';': '',
    });
    return encodeURI(slug).toLocaleLowerCase();
};

export const categoryPath = (category: string) =>
    modifyStringReplaceAll(category, {
        '&': 'and',
        '@': '',
    }).toLocaleLowerCase();

export const getProductUrl = (product: ProductHit, useSkuId = false) => {
    const categories = getPrimaryCategoryList(product.primary_category_id, product.categories);

    // slice() ensures a new array is made so that we are not mutating
    // it with reverse() was causing a hydration issue
    let primaryCategories = categories.slice();

    // Category array might sometimes come in order and other times
    // it might be reversed. The primary category should always be the
    // last element in the category hierarchy, so the array should
    // only be reversed if the primary category is found at the first index.
    if (primaryCategories.at(0)?.id === product.primary_category_id) {
        primaryCategories = primaryCategories.reverse();
    }

    const categoryHierarchy = primaryCategories.reduce(
        (acc: string, category: ProductCategory) => `${acc}/${categoryPath(toUrlString(category.name.default))}`,
        ''
    );

    const productName = toUrlString(product?.name || '');
    const productIdOrSku = useSkuId ? product.id : product.masterProductID;
    return `${categoryHierarchy}/${productName}-${productIdOrSku}.html`;
};

export const isNumericSKU = (productCode: string) => {
    // Regular expression to match a SKU: exactly 7 digits
    const skuRegex = /^\d{7}$/;
    return skuRegex.test(productCode);
};

export const transformHitForProductTile = (productHit: ProductHit, useVariantPrice = false) => {
    const hrefFormat = getProductUrl(productHit);
    const {
        id,
        name,
        short_description: shortDescription,
        images,
        price,
        bvAverageRating,
        bvReviewCount,
        badges,
        brand,
        masterProductID,
        variations,
        merchandisingHierarchy,
        __position,
        size,
        flavor,
        color,
        eligiblePromotions,
    } = productHit;

    const isRange = isPriceRange(price);

    const meta = getVariants(variations);
    const possibleCombinations = (meta ?? '')
        .match(/\d+/g)
        ?.map(num => parseInt(num, 10))
        .reduce((total, currentValue) => total * currentValue);

    const displayMeta = possibleCombinations && possibleCombinations > 1;

    return {
        _snippetResult: undefined,
        __position,
        id,
        brand,
        masterProductID,
        categoryList: getPrimaryCategoryList(productHit.primary_category_id, productHit.categories),
        objectID: `${id}`,
        title: decodeEntityString(name),
        description: shortDescription,
        href: hrefFormat,
        target: '_self',
        imgSrc: images?.large,
        imgAlt: name,
        priceVariant: getPriceVariant(price),
        prices: useVariantPrice && price ? [price?.number] : getPrices(price, isRange),
        isPriceRange: isRange,
        outOfStock: false,
        // this gets added from getProductsPromotions because of a needed call to SFCC
        promos: undefined,
        rating: bvAverageRating,
        ratingLabel: getReviewCount(bvReviewCount),
        badgeText: badges && badges.length > 0 ? badges[0] : undefined,
        badgeVariant: 'info',
        meta: displayMeta ? meta : '',
        rawProductHit: productHit,
        merchandisingHierarchy,
        itemSize: getItemSize(size as ProductSize),
        flavor,
        color,
        eligiblePromotions,
    };
};
